import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ToastContext from '../../../../../../context/ToastContext';
import useAxios from '../../../../../../hooks/useAxios';
import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import { useEndpointGuard } from '../../../../../../hooks/useEndpointGuard';
import useMediaQuery from '../../../../../../hooks/useMediaQuery';
import * as streetsGuards from '../../../../../../utils/constants/auth/streets';
import { WithData } from '../../../../../../types/api';
import { errorToast, successToast } from '../../../../../../utils/helpers/primereact';

type Street = {
  id: number;
  name: string;
};

type Props = {
  visible: boolean;
  onHide: () => void;
  placeId?: number | null;
  placeName?: string;
  onChanged?: () => void;
};

function ManageStreetsDialog({
  visible,
  onHide,
  placeId,
  placeName,
  onChanged,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const toastRef = useContext(ToastContext)?.toastRef!;
  const isOnMobile = useMediaQuery('(max-width: 768px)');
  const canCreate = useEndpointGuard(streetsGuards.createStreet);
  const canAddAltName = useEndpointGuard(streetsGuards.addStreetAltName);
  const canReplace = useEndpointGuard(streetsGuards.replaceStreet);

  const [query, setQuery] = useState<string>('');
  const [isAddStreetVisible, setIsAddStreetVisible] = useState<boolean>(false);
  const [newStreetName, setNewStreetName] = useState<string>('');
  const [aliasTargetId, setAliasTargetId] = useState<number | null>(null);
  const [aliasDraft, setAliasDraft] = useState<string>('');
  const [replaceTarget, setReplaceTarget] = useState<Street | null>(null);
  const [replaceWithId, setReplaceWithId] = useState<number | null>(null);

  const {
    data: streetsData,
    isLoading,
    reload: reloadStreets,
  } = useAxiosHook<WithData<Street[]>>(
    `/v2/streets?limit=0&placeId=${placeId}`,
    { skipWhen: !visible || !placeId, skipCache: true }
  );

  const { reload: sendRequest, isLoading: isSubmitting } = useAxios();

  const streets = useMemo<Street[]>(
    () => streetsData?.data ?? [],
    [streetsData]
  );

  useEffect(() => {
    if (visible) {
      setQuery('');
      setNewStreetName('');
      setAliasTargetId(null);
      setAliasDraft('');
    }
  }, [visible]);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    if (!q) return streets;
    return streets.filter((s) => s.name?.toLocaleLowerCase().includes(q));
  }, [streets, query]);

  const replaceOptions = useMemo(
    () =>
      streets
        .filter((s) => s.id !== replaceTarget?.id)
        .map((s) => ({ label: s.name, value: s.id })),
    [streets, replaceTarget]
  );

  const replaceWithName = useMemo(
    () => streets.find((s) => s.id === replaceWithId)?.name,
    [streets, replaceWithId]
  );

  function refresh() {
    reloadStreets({ url: `/v2/streets?limit=0&placeId=${placeId}` });
    onChanged?.();
  }

  function failureDetail(err: any, fallback: string): string {
    const status = err?.response?.status;

    if ([404, 405, 501].includes(status)) {
      return t(
        'This action is not available yet — the server does not support it. Please contact support.'
      );
    }

    if (status === 403) {
      return t('You do not have permission for this action.');
    }

    if (status === 409) {
      return t('That street is already linked to this populated area.');
    }

    const description = err?.response?.data?.error_description;

    return typeof description === 'string' && description
      ? description
      : fallback;
  }

  function handleAddStreet() {
    const name = newStreetName.trim();
    if (!name || !placeId) return;

    sendRequest({
      url: '/v2/streets',
      method: 'POST',
      payload: { name, placeId },
      successCallback: () => {
        successToast(
          toastRef,
          t('Street added'),
          t('Street added successfully.')
        );
        setNewStreetName('');
        setIsAddStreetVisible(false);
        refresh();
      },
      errorCallback: (err: any) => {
        errorToast(
          toastRef,
          t('Adding street failed'),
          failureDetail(err, t('Failed to add street.'))
        );
      },
    });
  }

  function handleAddAlias(street: Street) {
    const streetAltName = aliasDraft.trim();
    if (!streetAltName || !placeId) return;

    sendRequest({
      url: `/v2/streets/alt/${street.id}/${placeId}`,
      method: 'POST',
      payload: { streetAltName },
      successCallback: () => {
        successToast(
          toastRef,
          t('Alternative name added'),
          t('Alternative name added successfully.')
        );
        setAliasDraft('');
        setAliasTargetId(null);
        refresh();
      },
      errorCallback: (err: any) => {
        errorToast(
          toastRef,
          t('Adding alternative name failed'),
          failureDetail(err, t('Failed to add alternative name.'))
        );
      },
    });
  }

  function handleReplace() {
    if (!placeId || !replaceTarget || !replaceWithName) return;

    sendRequest({
      url: `/v2/streets/replace/${replaceTarget.id}/${placeId}`,
      method: 'POST',
      payload: { streetAltName: replaceWithName },
      successCallback: () => {
        successToast(
          toastRef,
          t('Street replaced'),
          t('Street replaced successfully.')
        );
        setReplaceTarget(null);
        setReplaceWithId(null);
        refresh();
      },
      errorCallback: (err: any) => {
        errorToast(
          toastRef,
          t('Replacing street failed'),
          failureDetail(err, t('Failed to replace street.'))
        );
      },
    });
  }

  const dialogFooter = (
    <div className="p-d-flex p-jc-between p-ai-center">
      <Button
        type="button"
        icon="pi pi-plus"
        label={t('Add street')}
        className="p-button-outlined"
        onClick={() => {
          setNewStreetName('');
          setIsAddStreetVisible(true);
        }}
        disabled={!placeId || isSubmitting || !canCreate}
        data-cy="open-add-street"
      />
      <Button
        type="button"
        label={t('Close')}
        className="p-button-text"
        onClick={onHide}
      />
    </div>
  );

  return (
    <>
      <Dialog
        visible={visible}
        onHide={onHide}
        header={t('Manage streets')}
        footer={dialogFooter}
        blockScroll
        maximizable
        maximized={isOnMobile}
        style={{ width: '640px', maxWidth: '95vw' }}
      >
        <p className="p-mb-3" style={{ color: '#6b7280' }}>
          {`${t('Place')}: ${placeName || '-'}`}
        </p>

        <span className="p-input-icon-left p-mb-3" style={{ width: '100%' }}>
          <i className="pi pi-search" />
          <InputText
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('Search')}
            style={{ width: '100%' }}
          />
        </span>

        {isLoading ? (
          <div className="p-d-flex p-jc-center p-py-4">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
          </div>
        ) : filtered.length === 0 ? (
          <p className="p-text-center p-py-4" style={{ color: '#6b7280' }}>
            {t('No streets found for this populated area')}
          </p>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              maxHeight: '340px',
              overflowY: 'auto',
            }}
          >
            {filtered.map((street) => (
              <li
                key={street.id}
                style={{ borderBottom: '1px solid #f0f1f4', padding: '8px 0' }}
              >
                <div className="p-d-flex p-jc-between p-ai-center">
                  <span>{street.name}</span>
                  <div className="p-d-flex p-ai-center">
                    <Button
                      type="button"
                      icon="pi pi-tag"
                      label={t('Alternative name')}
                      className="p-button-outlined p-button-sm p-mr-2"
                      onClick={() => {
                        setAliasDraft('');
                        setAliasTargetId(
                          aliasTargetId === street.id ? null : street.id
                        );
                      }}
                      disabled={isSubmitting || !canAddAltName}
                    />
                    <Button
                      type="button"
                      icon="pi pi-sort-alt"
                      label={t('Replace')}
                      className="p-button-outlined p-button-sm"
                      onClick={() => {
                        setReplaceWithId(null);
                        setReplaceTarget(street);
                      }}
                      disabled={isSubmitting || !canReplace}
                    />
                  </div>
                </div>

                {aliasTargetId === street.id && (
                  <div
                    className="p-d-flex p-ai-center p-mt-2"
                    style={{ borderLeft: '2px solid #e5e7eb', paddingLeft: 12 }}
                  >
                    <InputText
                      value={aliasDraft}
                      onChange={(e) => setAliasDraft(e.target.value)}
                      placeholder={t('Alternative name')}
                      disabled={isSubmitting}
                      style={{ flex: 1 }}
                    />
                    <Button
                      type="button"
                      label={t('Add')}
                      className="p-ml-2"
                      onClick={() => handleAddAlias(street)}
                      disabled={isSubmitting || !aliasDraft.trim()}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Dialog>

      <Dialog
        visible={isAddStreetVisible}
        onHide={() => setIsAddStreetVisible(false)}
        header={t('Add street')}
        style={{ width: '420px', maxWidth: '95vw' }}
        blockScroll
        footer={
          <>
            <Button
              type="button"
              label={t('Cancel')}
              className="p-button-text"
              onClick={() => setIsAddStreetVisible(false)}
            />
            <Button
              type="button"
              label={t('Add')}
              onClick={handleAddStreet}
              disabled={isSubmitting || !newStreetName.trim()}
              data-cy="add-street-btn"
            />
          </>
        }
      >
        <p className="p-mb-2" style={{ color: '#6b7280' }}>
          {`${t('Place')}: ${placeName || '-'}`}
        </p>
        <label htmlFor="newStreetName">{t('New street name')}</label>
        <InputText
          id="newStreetName"
          value={newStreetName}
          onChange={(e) => setNewStreetName(e.target.value)}
          disabled={isSubmitting}
          style={{ width: '100%' }}
        />
      </Dialog>

      <Dialog
        visible={!!replaceTarget}
        onHide={() => {
          setReplaceTarget(null);
          setReplaceWithId(null);
        }}
        header={t('Replace invalid street')}
        style={{ width: '460px', maxWidth: '95vw' }}
        blockScroll
        footer={
          <>
            <Button
              type="button"
              label={t('Cancel')}
              className="p-button-text"
              onClick={() => {
                setReplaceTarget(null);
                setReplaceWithId(null);
              }}
            />
            <Button
              type="button"
              label={t('Replace')}
              onClick={handleReplace}
              disabled={isSubmitting || !replaceWithId}
              data-cy="confirm-replace-street"
            />
          </>
        }
      >
        <p style={{ color: '#6b7280' }}>
          {t(
            'References to the invalid street will be moved to the correct street, and the invalid name will be kept as an alternative name. This change is permanent.'
          )}
        </p>

        <div className="p-mb-3">
          <label>{t('Invalid street name')}</label>
          <div style={{ fontWeight: 600 }}>{replaceTarget?.name}</div>
        </div>

        <div className="p-mb-3">
          <label htmlFor="replaceWithId">{t('Correct street')}</label>
          <Dropdown
            id="replaceWithId"
            options={replaceOptions}
            value={replaceWithId}
            onChange={(e) => setReplaceWithId(e.value ?? null)}
            filter
            filterPlaceholder={t('Search')}
            placeholder={t('Select Street')}
            showClear
            disabled={isSubmitting}
            style={{ width: '100%' }}
          />
        </div>

        <div
          className="p-d-flex p-ai-center"
          style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}
        >
          <span style={{ textDecoration: 'line-through', fontWeight: 500 }}>
            {replaceTarget?.name}
          </span>
          <i className="pi pi-arrow-right p-mx-2" style={{ color: '#9ca3af' }} />
          <span style={{ fontWeight: 500 }}>{replaceWithName || '—'}</span>
        </div>
      </Dialog>
    </>
  );
}

export default ManageStreetsDialog;
